import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import useOffersBestPrices from 'hooks/useOfferBestPrices'
import { buildSearchParamsKey, buildSuggestedDatesParamsKey } from 'lib/search/searchUtils'
import moment from 'moment'
import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { isBedbank } from 'lib/offer/offerTypes'
import LeMapSearchTile from './LeMapSearchTile'
import BedbankMapSearchTile from './BedbankMapSearchTile'

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates;
  bestPrices?: {
    [key: string]: App.OfferMaybeAvailableRate;
  };
  pricesErrors?: { [key: string]: any };
}

interface Props {
  offer:
    | App.Offer
    | App.OfferSummary
    | App.BedbankOffer
    | App.BedbankOfferSummary;
  filters?: App.OfferListFilters;
  onClose?: () => void;
}

function MapSearchTile(props: Props & MappedStateProps) {
  const { offer, filters, suggestedDates, onClose, bestPrices, pricesErrors } = props

  const searchKey = useMemo(() => buildSearchParamsKey(filters?.checkIn, filters?.checkOut, filters?.rooms), [filters])
  const bestPrice = bestPrices?.[searchKey]
  const bestPriceError = pricesErrors?.[searchKey]
  const available = bestPrice?.available
  const hasDates = !!(filters?.checkIn && filters?.checkOut)
  const rate = available ? bestPrice.rate : undefined

  const isBedbankOffer = isBedbank(offer)

  const bedbankLocation = useMemo(() => {
    if (!isBedbankOffer) {
      return undefined
    }

    const bedbankProperty = offer.property
    const addressArray: Array<string> = []
    const { city, countryName } = bedbankProperty.address
    if (city) addressArray.push(city)
    if (countryName) addressArray.push(countryName)

    return bedbankProperty.locationTitleOverride ?
      bedbankProperty.locationTitleOverride :
      addressArray.join(', ')
  }, [isBedbankOffer, offer.property])

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const offerUrl = useOfferUrl(offer, {
    filters,
    bundledOfferId: offerMetaData?.bundledOfferId,
    offerLinkIncludesFilters: true,
  })

  const location = isBedbankOffer ?
    bedbankLocation :
      [offer.locationHeading, offer.locationSubheading]
        .filter((t) => t)
        .join(', ')

  const filtersToSearch = {
    ...filters,
    checkIn: filters?.checkIn ?? suggestedDates?.checkIn,
    checkOut: filters?.checkOut ?? suggestedDates?.checkOut,
  }

  const checkInDate = filters?.checkIn ? moment(filters.checkIn) : undefined

  const { bestPricePackage, duration, bestPrice: price } = useOffersBestPrices(
    offer,
    filtersToSearch,
  )
  const fetchingPrice = hasDates && !bestPrice && !bestPriceError

  return (
    <div>
      {!isBedbankOffer && (
        <LeMapSearchTile
          onClose={onClose}
          offer={offer}
          location={location ?? ''}
          checkInDate={checkInDate}
          filters={filters ?? {}}
          offerUrl={offerUrl}
          bestPriceForDates={rate}
          bestPricePackage={bestPricePackage}
          fetchingPrice={fetchingPrice}
        />
      )}
      {isBedbankOffer && (
        <BedbankMapSearchTile
          onClose={onClose}
          filters={filters ?? {}}
          offer={offer}
          bestPrice={price}
          duration={duration}
        />
      )}
    </div>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (appState, ownProps): MappedStateProps => {
    const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(
      ownProps.filters?.flexibleMonths,
      ownProps.filters?.flexibleNights,
      ownProps.filters?.rooms,
    )
    return {
      suggestedDates: getSuggestedDates(
        appState,
        flexibleSearchFilterKey,
        ownProps.offer.id,
      ),
      bestPrices: appState.offer.offerBestPrices[ownProps.offer.id],
      pricesErrors: appState.offer.offerPricesErrors[ownProps.offer.id],
    }
  },
)(MapSearchTile)
